import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import type { Column } from "react-table";

import ReviewPackingListCommodityCell from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/cells/ReviewPackingListCommodityCell";
import ReviewPackingListHandlingCell from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/cells/ReviewPackingListHandlingCell";
import ReviewPackingListStopCell from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/cells/ReviewPackingListStopCell";
import ReviewPackingListUnitCell from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/cells/ReviewPackingListUnitCell";
import type { ReviewPackingListTableRow } from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/buildReviewPackingListRows";
import type { ReviewStopDisplay } from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/useReviewStopDisplay";
import {
  formatAdditionalInformation,
  type ReviewPackingListLabelContext,
} from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/reviewPackingListFormatters";

const STOP_COLUMN_WIDTH = 260;

export function useReviewPackingListColumns(
  intl: IntlShape,
  labelContext: ReviewPackingListLabelContext,
  isMultiStop: boolean,
  stopDisplayByKey: Map<string, ReviewStopDisplay>,
): Column<ReviewPackingListTableRow>[] {
  return useMemo(
    () => [
      {
        id: "rowNumber",
        Header: intl.formatMessage({
          id: "request-form.review.packingList.no",
          defaultMessage: "No.",
        }),
        accessor: "rowNumber",
        width: 40,
        Cell: ({ value }: { value: number }) => <span>{value}</span>,
      },
      {
        id: "unit",
        Header: intl.formatMessage({
          id: "request-form.review.packingList.unit",
          defaultMessage: "Unit",
        }),
        width: 140,
        Cell: ({ row }: { row: { original: ReviewPackingListTableRow } }) => (
          <ReviewPackingListUnitCell row={row.original.row} labelContext={labelContext} />
        ),
      },
      {
        id: "qty",
        Header: intl.formatMessage({
          id: "request-form.review.packingList.qty",
          defaultMessage: "Qty",
        }),
        accessor: (row: ReviewPackingListTableRow) => row.row.units,
        width: 50,
        Cell: ({ value }: { value: number | null | undefined }) => <span>{value ?? "-"}</span>,
      },
      {
        id: "handling",
        Header: intl.formatMessage({
          id: "request-form.review.packingList.handling",
          defaultMessage: "Handling",
        }),
        Cell: ({ row }: { row: { original: ReviewPackingListTableRow } }) => (
          <ReviewPackingListHandlingCell row={row.original.row} labelContext={labelContext} />
        ),
      },
      ...(isMultiStop
        ? [
            {
              id: "pickup",
              Header: intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" }),
              width: STOP_COLUMN_WIDTH,
              Cell: ({ row }: { row: { original: ReviewPackingListTableRow } }) => (
                <ReviewPackingListStopCell stopKey={row.original.row.pickup_stop_key} stopDisplayByKey={stopDisplayByKey} />
              ),
            },
            {
              id: "delivery",
              Header: intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" }),
              width: STOP_COLUMN_WIDTH,
              Cell: ({ row }: { row: { original: ReviewPackingListTableRow } }) => (
                <ReviewPackingListStopCell
                  stopKey={row.original.row.delivery_stop_key}
                  stopDisplayByKey={stopDisplayByKey}
                />
              ),
            },
          ]
        : []),
      {
        id: "commodityInformation",
        Header: intl.formatMessage({
          id: "request-form.review.packingList.commodityInformation",
          defaultMessage: "Commodity information",
        }),
        Cell: ({ row }: { row: { original: ReviewPackingListTableRow } }) => (
          <ReviewPackingListCommodityCell commodities={row.original.commodities} />
        ),
      },
      {
        id: "additionalInformation",
        Header: intl.formatMessage({
          id: "request-form.review.packingList.additionalInformation",
          defaultMessage: "Additional information",
        }),
        width: 182,
        Cell: ({ row }: { row: { original: ReviewPackingListTableRow } }) => (
          <span>{formatAdditionalInformation(row.original.row)}</span>
        ),
      },
    ],
    [intl, labelContext, isMultiStop, stopDisplayByKey],
  );
}
